import type { LodeomTypeCode, Origine, ProdCode, StatutCode } from '../api/types';

/**
 * ⚠️ PREVIEW UNIQUEMENT — fidèle à calcCommission() du proto HTML.
 *
 * Cette fonction sert à afficher une estimation à l'utilisateur pendant la saisie.
 * La valeur faisant FOI est toujours celle calculée par le backend.
 * Si les règles divergent, le backend gagne.
 */
export interface CommissionPreviewInput {
  prod: ProdCode;
  montant: number;
  statut: StatutCode;
  origine: Origine;
  frais?: number | null;       // PU/PP
  lodeomType?: LodeomTypeCode | null;
  /** Override taux % pour immo-vente. Null/undef = défaut Leads (2.5) ou Reco (4). */
  tauxImmoVente?: number | null;
}

export function previewCommission(i: CommissionPreviewInput): number {
  const isMia = i.statut === 'mia';
  const m = i.montant;
  const frais = i.frais ?? 3.5;

  let raw = 0;
  switch (i.prod) {
    case 'pu':
      raw = m * (frais / 100 - 0.01);
      break;
    case 'pp': {
      const c = frais >= 3.95 ? 2.5 : 1.0;
      raw = m * c;
      break;
    }
    case 'lodeom': {
      const t = i.lodeomType === 'reab' ? 0.02 : 0.03;
      raw = m * t;
      break;
    }
    case 'scpi':
      raw = m * 0.025;
      break;
    case 'immo-vente': {
      // Si l'user a saisi un taux manuel, on le prend ; sinon défaut 4 (reco) / 2.5 (leads)
      const tauxPct = i.tauxImmoVente ?? (i.origine === 'reco' ? 4 : 2.5);
      raw = m * (tauxPct / 100);
      break;
    }
    case 'immo-apport':
      // Cas SPÉCIAL : salarié = 0,75% (pas 1,5% / 2)
      return Math.max(0, isMia ? m * 0.015 : m * 0.0075);
  }

  if (raw < 0) raw = 0;
  return isMia ? raw : raw / 2;
}

/** Pour la PP, le montant saisi est mensuel → collecte annualisée × 12. */
export function previewCollecte(prod: ProdCode, montant: number): number {
  return prod === 'pp' ? montant * 12 : montant;
}

/** Score d'une vente individuelle. */
export function previewScore(commission: number, collecte: number): number {
  return Math.round(commission * 0.5 + collecte * 0.001 + 10);
}

/** Détermine si un produit nécessite un champ "frais". */
export function requiresFrais(prod: ProdCode): boolean {
  return prod === 'pu' || prod === 'pp';
}

/**
 * Renvoie la formule de calcul commission en texte mono-line.
 * Exemples :
 *   - PU avec frais 3.5%, MIA : "Montant × (3,5% − 1%)"
 *   - PU avec frais 3.5%, VRP : "Montant × (3,5% − 1%) ÷ 2"
 *   - PP frais 4%, MIA       : "VP mensuel × 2,5"
 *   - immo-apport VRP        : "Prix de vente × 0,75%"
 */
export function formulaText(i: {
  prod: ProdCode;
  statut: StatutCode;
  origine: Origine;
  frais?: number | null;
  lodeomType?: LodeomTypeCode | null;
  tauxImmoVente?: number | null;
}): string {
  const isMia = i.statut === 'mia';
  const div = isMia ? '' : ' ÷ 2 (VRP)';
  const frais = i.frais ?? 3.5;

  switch (i.prod) {
    case 'pu': {
      const pct = (frais - 1).toFixed(2).replace('.', ',');
      const fr = String(frais).replace('.', ',');
      return `Montant × (${fr}% − 1%) = Montant × ${pct}%${div}`;
    }
    case 'pp': {
      const c = frais >= 3.95 ? '2,5' : '1,0';
      return `VP mensuel × ${c}${div}`;
    }
    case 'lodeom': {
      const t = i.lodeomType === 'reab' ? '2%' : '3%';
      return `Montant souscrit × ${t}${div}`;
    }
    case 'scpi':
      return `Montant souscrit × 2,5%${div}`;
    case 'immo-vente': {
      // Si taux manuel saisi → on l'affiche directement, sinon défaut Leads/Reco
      const tauxNum = i.tauxImmoVente ?? (i.origine === 'reco' ? 4 : 2.5);
      const tauxStr = `${String(tauxNum).replace('.', ',')}%`;
      const src = i.tauxImmoVente != null
        ? 'Taux personnalisé'
        : (i.origine === 'reco' ? 'Recommandation' : 'Leads');
      return `Prix de vente × ${tauxStr} (${src})${div}`;
    }
    case 'immo-apport':
      return `Prix de vente × ${isMia ? '1,5%' : '0,75%'}`;
  }
}

/**
 * Renvoie l'info taux affichée dans le bandeau 💡 (taux appliqué).
 * Exemple : "Leads · 2,5% du prix de vente (÷2 VRP)"
 */
export function tauxInfoText(i: {
  prod: ProdCode;
  statut: StatutCode;
  origine: Origine;
  frais?: number | null;
  lodeomType?: LodeomTypeCode | null;
  tauxImmoVente?: number | null;
}): string {
  const isMia = i.statut === 'mia';
  const vrpSuffix = isMia ? '' : ' (÷2 VRP)';
  const frais = i.frais ?? 3.5;

  switch (i.prod) {
    case 'pu':
      return `Frais d'entrée ${frais}% · commission = montant × (frais − 1%)${vrpSuffix}`;
    case 'pp': {
      const c = frais >= 3.95 ? '×2,5' : '×1,0';
      const tranche = frais >= 3.95 ? '≥ 3,95%' : '< 3,95%';
      return `Frais ${frais}% → tranche ${tranche} → coefficient ${c}${vrpSuffix}`;
    }
    case 'lodeom': {
      const t = i.lodeomType === 'reab' ? '2%' : '3%';
      const lbl = i.lodeomType === 'reab' ? 'Ré-abondement' : 'Nouveau client';
      return `${lbl} · ${t} du montant souscrit${vrpSuffix}`;
    }
    case 'scpi':
      return `SCPI · 2,5% du montant souscrit${vrpSuffix}`;
    case 'immo-vente': {
      if (i.tauxImmoVente != null) {
        // Taux manuel saisi
        const tauxStr = `${String(i.tauxImmoVente).replace('.', ',')}%`;
        const defaut = i.origine === 'reco' ? '4 %' : '2,5 %';
        const src = i.origine === 'reco' ? 'Recommandation' : 'Leads';
        return `Taux personnalisé ${tauxStr} du prix de vente (défaut ${src} : ${defaut})${vrpSuffix}`;
      }
      const t = i.origine === 'reco' ? '4,0%' : '2,5%';
      const src = i.origine === 'reco' ? 'Recommandation' : 'Leads';
      return `${src} · ${t} du prix de vente${vrpSuffix}`;
    }
    case 'immo-apport':
      return `Apport d'affaires · ${isMia ? '1,5%' : '0,75%'} du prix de vente`;
  }
}

/** Liste des dispositifs immobiliers (ne change pas la commission). */
export const DISPOSITIFS = [
  'Accession', 'Déficit foncier', 'Denormandie', 'Investissement',
  'LLI', 'LMNP', 'Malraux', 'MH', 'Nue propriété', 'Autre'
] as const;

/** Liste figée des agences. */
export const AGENCES = [
  'Agence Nationale',
  'Bayonne', 'Clermont', 'Lille', 'Lyon', 'Montpellier','Nantes',
  'Nice', 'Paris', 'Paris Est', 'Paris Ouest', 'Reims',
  'Rennes', 'Soronext', 'Strasbourg', 'Toulouse', 'Tours'
] as const;
